import { useTableSort } from '@flexinet/ui-components';
import { DataTableSortStatus } from 'mantine-datatable';

import { usePipelinesQuery } from './usePipelinesQuery';
import { usePipelinesSearch } from './usePipelinesSearch';
import { Pipeline } from '../types';

// DEFAULT: newest first, keyed on `created`.
//
// ⚠️ `created` is NULLABLE on Pipeline (types.ts), which is not what the
// library's guidance prefers for a default key — a nullable key degrades to
// insertion order if EVERY row lacks it. There is no alternative here: `id` is
// the only non-null field on the type, and sorting a list of pipelines by an
// opaque id is meaningless to an operator.
//
// `created` is the right key despite that. It is immutable (unlike `updated`,
// which would reorder the list under someone reading it), and listPipelines
// already returns created-descending, so the first paint AGREES with the server
// rather than reshuffling it. Rows missing it sort last, which is correct.
//
// If Pipeline.created is ever made non-null in the API schema, this comment is
// the only thing that needs to change.
const DEFAULT_SORT_STATUS: DataTableSortStatus<Pipeline> = {
    columnAccessor: 'created',
    direction: 'desc',
};

export const usePipelines = (filters: Record<string, string> = {}) => {
    const query = usePipelinesQuery(filters);
    const { keyword } = usePipelinesSearch();

    const items: Pipeline[] = query.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) ?? [];

    // ⚠️ Both the keyword filter and the sort below cover only the pages
    // FETCHED SO FAR. Under token pagination the browser cannot filter or
    // reorder rows it has never seen, so the footer states a LOADED count
    // rather than a total, and `unfilteredCount` is reported alongside the
    // match count so the operator can see the search was scoped to a window.
    const needle = keyword.trim().toLowerCase();

    const matched = items.filter(item =>
        !needle ||
        item.name?.toLowerCase().includes(needle) ||
        item.description?.toLowerCase().includes(needle) ||
        item.codeRepositoryName?.toLowerCase().includes(needle)
    );

    // Sort state is component-level, not the module-scoped store: sort is how
    // ONE operator is viewing the table right now, unlike the keyword and the
    // filters, which are shared with sibling components.
    const {
        sortStatus,
        setSortStatus,
        sorted: visibleItems,
    } = useTableSort<Pipeline>(matched, DEFAULT_SORT_STATUS);

    const fetchNextPage = () => {
        if (query.hasNextPage) {
            query.fetchNextPage();
        }
    };

    return {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        unfilteredCount: items.length,
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage,
    };
};
